import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DatabaseCacheService, Section } from '../../../data/cache/database-cache.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { PermissionService } from '@app/core/auth/permission.service';
import { BaseViewModel, BaseViewModelState } from '../../../shared/view-model/base-view-model';

export interface HomeNavViewModelState extends BaseViewModelState {
  sections: Section[];
  selectedSection: Section | null;
  confirmedSelectedSection: Section | null;
  selectedSectionGroup: any;
  selectedSectionWithRole: any;
  sectionGroupDocuments: any[];
  loginUserName: string;
  isLoggedIn: boolean;
  loginUserJob: string;
  loginUserJobCode: string;
  accountManagementRole: boolean;
  userManagementRole: boolean;
  recordMeasurerRole: boolean;
  recordRefererRole: boolean;
  sectionGroupAdminRole: boolean;
  systemAdminRole: boolean;
  distributorAgentRole: boolean;
  contractEnabled: boolean;
  showMentionMenu: boolean;
  showTomassMenu: boolean;
  currentLocale: string | null;
  /** True while switching sections and loading user list */
  isSectionLoading: boolean;
}

const initialState: HomeNavViewModelState = {
  status: 'idle',
  errorMessage: null,
  sections: [],
  selectedSection: null,
  confirmedSelectedSection: null,
  selectedSectionGroup: null,
  selectedSectionWithRole: null,
  sectionGroupDocuments: [],
  loginUserName: '',
  isLoggedIn: false,
  loginUserJob: '',
  loginUserJobCode: '',
  accountManagementRole: false,
  userManagementRole: false,
  recordMeasurerRole: false,
  recordRefererRole: false,
  sectionGroupAdminRole: false,
  systemAdminRole: false,
  distributorAgentRole: false,
  contractEnabled: false,
  showMentionMenu: false,
  showTomassMenu: false,
  currentLocale: null,
  isSectionLoading: false,
};

@Injectable()
export class HomeNavViewModel extends BaseViewModel<HomeNavViewModelState> {
  private readonly permission = inject(PermissionService);

  /** Tracks the sectionId we're currently loading to prevent premature loading state clear */
  private loadingSectionId: string | null = null;

  /** Safety timeout to prevent skeleton from being stuck indefinitely (30 seconds) */
  private loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private static readonly LOADING_TIMEOUT_MS = 30000;

  constructor(
    private readonly common: DatabaseCacheService,
    private readonly storeAuth: StoreAuthService,
    private readonly destroyRef: DestroyRef,
  ) {
    super(initialState);

    this.storeAuth.getLoginUserNameObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(name => this.patchState({ loginUserName: name || '' }));

    this.storeAuth.getIsLoggedInOservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isLoggedIn => this.patchState({ isLoggedIn }));
  }

  init(): void {
    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshFromCache());

    this.refreshFromCache();
  }

  setCurrentLocale(locale: string | null): void {
    this.patchState({ currentLocale: locale });
  }

  async setSectionById(sectionId: string): Promise<void> {
    await this.common.setSelectedSectionById(sectionId);
    this.refreshFromCache();
  }

  async selectSection(section: Section): Promise<void> {
    await this.common.setSelectedSection(section);
    this.refreshFromCache();
  }

  /**
   * Pre-set loading state for section transition.
   * IMPORTANT: Only sets isSectionLoading flag, does NOT update contractEnabled or selectedSection.
   * This prevents UI flash by keeping current view stable until new data is fully loaded.
   * Called before navigation to show skeleton overlay during transition.
   */
  preSelectSection(sectionId: string): void {
    const sectionDocuments = this.common.getSectionDocuments();
    if (!sectionDocuments) {
      return;
    }

    const sectionDoc = sectionDocuments.find((doc: { id: string }) => doc.id === sectionId);
    if (!sectionDoc) {
      return;
    }

    // Determine contract status from cached sectionDocuments (for loading state decision only)
    const contractStatus = sectionDoc.contractInfo?.contractStatus;
    const targetContractEnabled = contractStatus !== 'CLOSE';

    // Check if this is actually a section change (not just re-selecting the same section)
    const isChangingSection = this.currentState.selectedSection?.sectionId !== sectionId;

    // Only show skeleton when:
    // 1. Actually changing sections AND
    // 2. Target section has active contract (needs to load users list)
    if (isChangingSection && targetContractEnabled) {
      this.loadingSectionId = sectionId;

      // Clear any existing timeout and set a new safety timeout
      this.clearLoadingTimeout();
      this.loadingTimeoutId = setTimeout(() => {
        console.warn(`[HomeNavViewModel] Loading timeout reached for section ${sectionId}, forcing clear`);
        this.forceCleanupLoadingState();
      }, HomeNavViewModel.LOADING_TIMEOUT_MS);

      // ONLY set loading state - do NOT update contractEnabled or selectedSection here
      // This keeps the current view stable (skeleton overlays on top) until refreshFromCache()
      // updates everything atomically when the new section's data is ready
      this.patchState({
        isSectionLoading: true,
      });
    }
  }

  /**
   * Force cleanup of loading state. Called on timeout or navigation errors.
   */
  private forceCleanupLoadingState(): void {
    this.loadingSectionId = null;
    this.clearLoadingTimeout();
    this.patchState({ isSectionLoading: false });
  }

  /**
   * Public method to clear loading state.
   * Called from component on NavigationCancel/NavigationError events.
   */
  clearLoadingState(): void {
    this.forceCleanupLoadingState();
  }

  /**
   * Called when navigation completes (NavigationEnd).
   * Only clears loading state if the sectionId matches the one we're loading.
   * This ensures skeleton stays visible until route transition actually completes.
   */
  onNavigationComplete(sectionId: string | null): void {
    // Clear loading state only if we were tracking this section
    if (this.loadingSectionId && this.loadingSectionId === sectionId) {
      this.forceCleanupLoadingState();
    }
  }

  /**
   * Clear the loading timeout if set.
   */
  private clearLoadingTimeout(): void {
    if (this.loadingTimeoutId) {
      clearTimeout(this.loadingTimeoutId);
      this.loadingTimeoutId = null;
    }
  }

  confirmSelection(): void {
    this.patchState({
      confirmedSelectedSection: this.currentState.selectedSection,
    });
  }

  private refreshFromCache(): void {
    const selectedSectionGroup = this.common.getSelectedSectionGroup();
    const selectedSection = this.common.getSelectedSection();
    const sections = this.common.getSectionList();
    const selectedSectionWithRole = this.common.getSelectedSectionWithRole();
    const sectionGroupDocuments = this.common.getSectionGroupDocuments();
    const contractEnabled = this.common.getSelectedSectionContractEnabled();
    const userJobTitleCode = this.common.getSelectedSectionJobTitleCode();
    const loginUserJob = this.common.getJobTitle(userJobTitleCode);
    const usersList = this.common.getUsersList();

    const roles: string[] = selectedSectionWithRole?.permissions?.role ?? [];
    const accountManagementRole = roles.includes('ACCOUNT_MANAGEMENT');
    const userManagementRole = roles.includes('USER_MANAGEMENT');
    const recordMeasurerRole = roles.includes('MEASUREMENT');
    const recordRefererRole = roles.includes('REFER');

    const systemAdminState = this.getSystemAdminState();
    const sectionGroupAdminRole =
      systemAdminState.grantsSectionGroupAdmin || this.hasSectionGroupAdminRole(selectedSectionWithRole);
    const systemAdminRole = systemAdminState.isSystemAdmin;
    const distributorAgentRole = this.common.hasAnyDistributorAgentRole();

    if (userJobTitleCode) {
      this.storeAuth.setJobTitleCode(userJobTitleCode);
    }

    // Determine if loading state should be cleared
    // usersList is set to null during loading, and becomes an array when loaded
    // Note: usersList was already fetched above for logging
    const currentSectionId = selectedSection?.sectionId;

    // Calculate shouldSetLoading based on state
    // IMPORTANT: We do NOT clear loading state here anymore.
    // Loading state is only cleared by:
    // 1. onNavigationComplete() - when NavigationEnd occurs
    // 2. clearLoadingState() - when NavigationCancel/Error occurs
    // 3. Timeout - safety mechanism
    // This ensures skeleton stays visible until route transition actually completes.
    let shouldSetLoading = false;

    if (!this.loadingSectionId) {
      // No specific section being tracked (URL direct access, back/forward navigation, etc.)
      // Fallback logic: show loading when contractEnabled=true but usersList is still loading
      const isUsersListLoaded = usersList !== null; // null = loading, array = loaded

      if (contractEnabled && currentSectionId && !isUsersListLoaded && !this.currentState.isSectionLoading) {
        // Data is still loading and we're not in loading state - set it now
        // This covers routes that bypass preSelectSection (URL direct, back/forward)
        // Also set loadingSectionId and timeout to prevent stuck skeleton
        this.loadingSectionId = currentSectionId;
        this.clearLoadingTimeout();
        this.loadingTimeoutId = setTimeout(() => {
          console.warn(`[HomeNavViewModel] Fallback loading timeout reached for section ${currentSectionId}, forcing clear`);
          this.forceCleanupLoadingState();
        }, HomeNavViewModel.LOADING_TIMEOUT_MS);

        shouldSetLoading = true;
      }
    }

    // Only SET loading state, never clear it here (clearing is done via navigation events)
    const newIsSectionLoading = shouldSetLoading ? true : this.currentState.isSectionLoading;

    this.patchState({
      status: 'success',
      sections: sections?.length ? sections : [{ sectionId: '--', sectionName: '--' } as Section],
      selectedSection,
      confirmedSelectedSection: selectedSection,
      selectedSectionGroup,
      selectedSectionWithRole,
      sectionGroupDocuments: sectionGroupDocuments ?? [],
      contractEnabled: !!contractEnabled,
      loginUserJobCode: userJobTitleCode ?? '',
      loginUserJob,
      accountManagementRole,
      userManagementRole,
      recordMeasurerRole,
      recordRefererRole,
      sectionGroupAdminRole,
      systemAdminRole,
      distributorAgentRole,
      // Use PermissionService for feature checks
      // showMentionMenu: checks all section groups (cross-section feature)
      showMentionMenu: this.permission.hasFeatureInAnySectionGroup('Dialog'),
      // showTomassMenu: checks current section group only
      showTomassMenu: this.permission.hasFeature('TOMASS_SUM'),
      // Manage loading state (pre-computed above with logging)
      isSectionLoading: newIsSectionLoading,
    });
  }

  private hasSectionGroupAdminRole(selectedSectionWithRole: any): boolean {
    try {
      const sections = this.common.getSectionDocuments();
      const selectedSection = sections.find(s => s.sectionId === selectedSectionWithRole?.sectionId);
      const sectionGroups = this.common.getSectionGroupDocuments();
      const selectedSectionGroup = selectedSection
        ? sectionGroups.find(s => s.id === selectedSection.sectionGroupId)
        : undefined;
      if (!selectedSection || !selectedSectionGroup) {
        return false;
      }
      const sectionGroupAdmins = this.common.getSectionGroupAdmins();
      const selectedSectionGroupAdmin = sectionGroupAdmins.find(s => s.sectionGroupId === selectedSectionGroup.id);
      return !!selectedSectionGroupAdmin;
    } catch {
      return false;
    }
  }

  private getSystemAdminState(): { isSystemAdmin: boolean; grantsSectionGroupAdmin: boolean } {
    try {
      const systemAdmin = this.common.getSystemAdmin();
      if (!systemAdmin) {
        return { isSystemAdmin: false, grantsSectionGroupAdmin: false };
      }
      const grants = !!systemAdmin.roles?.find((r: string) => r === 'ALL_SECTION_GROUP_MANAGEMENT');
      return { isSystemAdmin: true, grantsSectionGroupAdmin: grants };
    } catch {
      return { isSystemAdmin: false, grantsSectionGroupAdmin: false };
    }
  }

}
